import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Truck } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { usePageHead } from "@/lib/siteContent";
import { API } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

// Supplier-facing page from the PO email: confirm the order and tell us the ship date.
export default function SupplierPoConfirm() {
  const site = useSite();
  const { token } = useParams();
  const [po, setPo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ ship_date: "", note: "", contact_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  usePageHead({ title: po ? `Confirm ${po.code}` : "Confirm purchase order" });

  useEffect(() => {
    fetch(`${API}/public/po-confirm/${token}`)
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then((data) => {
        setPo(data);
        setForm((f) => ({
          ...f,
          ship_date: data.confirmation?.ship_date || data.expected_date || today(),
          contact_name: data.confirmation?.contact_name || "",
          note: data.confirmation?.note || "",
        }));
      })
      .catch((e) => setError(e?.detail || "This order link is not valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/public/po-confirm/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setPo(data);
      setDone(true);
    } catch (err) {
      setError(err?.detail || "Could not save your confirmation. Please try again.");
    }
    setBusy(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field =
    "mt-1 w-full  border border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-3 py-3 text-base text-[color:var(--s-ink)] outline-none focus:border-[color:var(--s-gold)]";

  return (
    <SiteShell site={site} testId="supplier-po-confirm">
      <main className="px-6 pb-24 pt-6 sm:pt-8 lg:px-12" data-testid="po-confirm-page">
        <div className="mx-auto max-w-2xl">
          <span className="s-micro inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
            Purchase order confirmation
          </span>

          {error && !po && (
            <div className="mt-6  border border-red-300 bg-red-50 p-5 text-red-800" data-testid="po-confirm-error">
              {error}
            </div>
          )}

          {po && (
            <>
              <h1 className="s-display mt-4 text-3xl sm:text-4xl" data-testid="po-confirm-headline">
                {po.code} from {po.shop.name || "our shop"}
              </h1>
              <p className="mt-2 text-[color:var(--s-mute)]">
                For {po.supplier_name || "you"}
                {po.order_date ? ` · ordered ${po.order_date}` : ""}
                {po.reference ? ` · ref ${po.reference}` : ""}
              </p>

              <div className="s-card mt-8 !p-5" data-testid="po-confirm-lines">
                <div className="s-micro">Items</div>
                <ul className="mt-3 divide-y divide-[color:var(--s-line)]">
                  {po.lines.map((l, i) => (
                    <li key={i} className="flex items-start justify-between gap-4 py-2.5">
                      <div>
                        <div className="font-semibold">{l.name}</div>
                        {l.detail && (
                          <div className="text-sm text-[color:var(--s-mute)]">{l.detail}</div>
                        )}
                      </div>
                      <div className="text-right font-semibold">
                        × {l.qty}
                        {l.sqft != null && (
                          <div className="text-xs font-normal text-[color:var(--s-mute)]">
                            {Number(l.sqft).toLocaleString("en-US", { maximumFractionDigits: 1 })} ft²
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {po.closed ? (
                <div className="mt-8  border border-[color:var(--s-line)] bg-[color:var(--s-paper)] p-5" data-testid="po-confirm-closed">
                  This order is already {po.status.toLowerCase()} — nothing more to confirm. Thank you!
                </div>
              ) : done ? (
                <div className="mt-8  border border-emerald-300 bg-emerald-50 p-6" data-testid="po-confirm-done">
                  <div className="flex items-center gap-3 text-emerald-800">
                    <CheckCircle size={28} weight="fill" />
                    <div className="text-lg font-semibold">Thank you — order confirmed</div>
                  </div>
                  <p className="mt-2 text-emerald-900">
                    {po.confirmation?.ship_date
                      ? `We've noted a ship date of ${po.confirmation.ship_date}.`
                      : "We've noted your confirmation."}{" "}
                    {po.shop.name} has been updated. You can revisit this link to change the date.
                  </p>
                  <button
                    type="button"
                    data-testid="po-confirm-edit-again"
                    onClick={() => setDone(false)}
                    className="s-ghost mt-4"
                  >
                    Change the ship date
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="s-card mt-8 !p-6" data-testid="po-confirm-form">
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <Truck size={20} weight="bold" className="text-[color:var(--s-gold)]" />
                    Confirm &amp; set your ship date
                  </div>
                  {po.confirmation && (
                    <p className="mt-1 text-sm text-[color:var(--s-mute)]" data-testid="po-confirm-previous">
                      Previously confirmed {po.confirmation.at?.slice(0, 10)}
                      {po.confirmation.ship_date ? ` · ship date ${po.confirmation.ship_date}` : ""}
                    </p>
                  )}
                  <label className="mt-5 block text-sm font-semibold">
                    Ship date
                    <input
                      data-testid="po-confirm-ship-date"
                      type="date"
                      required
                      value={form.ship_date}
                      onChange={set("ship_date")}
                      className={field}
                    />
                  </label>
                  <label className="mt-4 block text-sm font-semibold">
                    Your name (optional)
                    <input
                      data-testid="po-confirm-contact"
                      value={form.contact_name}
                      onChange={set("contact_name")}
                      placeholder="Who is confirming"
                      className={field}
                    />
                  </label>
                  <label className="mt-4 block text-sm font-semibold">
                    Note for {po.shop.name || "the shop"} (optional)
                    <textarea
                      data-testid="po-confirm-note"
                      rows={3}
                      value={form.note}
                      onChange={set("note")}
                      placeholder="Partial ship, carrier, tracking number…"
                      className={field}
                    />
                  </label>
                  {error && (
                    <p className="mt-3 text-sm text-red-700" data-testid="po-confirm-form-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="po-confirm-submit"
                    disabled={busy}
                    className="s-btn mt-6 w-full justify-center"
                  >
                    {busy ? "Saving…" : po.confirmation ? "Update ship date" : "Confirm order"}
                  </button>
                </form>
              )}

              {(po.shop.phone || po.shop.email) && (
                <p className="mt-6 text-sm text-[color:var(--s-mute)]">
                  Questions? {po.shop.phone && <span>Call {po.shop.phone}</span>}
                  {po.shop.phone && po.shop.email && " · "}
                  {po.shop.email && <a href={`mailto:${po.shop.email}`} className="underline">{po.shop.email}</a>}
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </SiteShell>
  );
}
